/**
 * The console's HTTP client, and the shapes /admin returns.
 *
 * The admin key is a staff credential, so it lives in this module's closure for
 * the lifetime of the tab and nowhere else: not localStorage, not
 * sessionStorage, not a cookie. Closing the tab signs you out, and that cost is
 * the point — every read here writes an audit row against a named actor (§07),
 * which only means something if the credential cannot be left lying around on a
 * shared moderation workstation.
 *
 * Nothing in this file logs a request, a header or a response body. There is no
 * console.* anywhere in the console for the same reason the API never returns a
 * phone number (§07).
 */

import type { Plan } from '@alap/shared';

/* ------------------------------------------------------------------ *
 * Configuration
 * ------------------------------------------------------------------ */

const FALLBACK_BASE = 'http://localhost:4000';

const configuredBase: unknown = import.meta.env.VITE_API_BASE_URL;

export const apiBase: string = (typeof configuredBase === 'string' && configuredBase.length > 0
  ? configuredBase
  : FALLBACK_BASE
).replace(/\/+$/, '');

/* ------------------------------------------------------------------ *
 * Credential — memory only
 * ------------------------------------------------------------------ */

let adminKey: string | null = null;
let actorHandle: string | null = null;

export function setCredential(key: string, actor: string): void {
  adminKey = key;
  actorHandle = actor.trim().slice(0, 120) || null;
}

export function clearCredential(): void {
  adminKey = null;
  actorHandle = null;
}

/**
 * The shell registers here so that a key rejected mid-session drops the whole
 * console back to the gate, rather than leaving four screens each showing their
 * own 401. Registered once, in App.
 */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

export function hasCredential(): boolean {
  return adminKey !== null;
}

/** The handle that lands in the audit log. Never the key. */
export function currentActor(): string | null {
  return actorHandle;
}

/* ------------------------------------------------------------------ *
 * Errors
 * ------------------------------------------------------------------ */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: unknown;

  constructor(status: number, code: string, message: string, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
  }

  /** True when the key was rejected — the shell drops back to the gate. */
  get isAuth(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function fallbackMessage(status: number): string {
  if (status === 404) return 'That is not there any more.';
  if (status === 429) return 'The API is rate limiting this console. Wait a minute.';
  if (status >= 500) return 'The API broke on its side. Try again in a moment.';
  return `The API refused the request (HTTP ${status}).`;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (adminKey === null) {
    throw new ApiError(401, 'locked', 'The console is locked. Enter the admin key again.');
  }

  const headers: Record<string, string> = {
    accept: 'application/json',
    'x-alap-admin-key': adminKey,
  };
  if (actorHandle !== null) headers['x-alap-admin-actor'] = actorHandle;
  if (body !== undefined) headers['content-type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(`${apiBase}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      // No cookies anywhere near this. The key header is the only credential.
      credentials: 'omit',
      cache: 'no-store',
    });
  } catch {
    throw new ApiError(
      0,
      'unreachable',
      `Could not reach the API at ${apiBase}. Check that it is running and that VITE_API_BASE_URL points at it.`,
    );
  }

  const text = await res.text();
  const parsed = text.length > 0 ? safeJson(text) : null;

  if (!res.ok) {
    const shape = parsed as { error?: string; message?: string; detail?: unknown } | null;
    if (res.status === 401 || res.status === 403) {
      clearCredential();
      onUnauthorized?.();
    }
    throw new ApiError(
      res.status,
      shape?.error ?? `http_${res.status}`,
      shape?.message ?? fallbackMessage(res.status),
      shape?.detail,
    );
  }

  return parsed as T;
}

/* ------------------------------------------------------------------ *
 * Response shapes (mirrors apps/api/src/routes/admin.ts)
 * ------------------------------------------------------------------ */

/** Azure AI Foundry's read on a case. Advisory, always (§08). */
export interface TriageAdvisory {
  advisory: boolean;
  summary: string;
  suggestedVerdict: string;
  confidence: number;
  /** 'azure_foundry' when the model answered, 'heuristic' when it is the offline fallback. */
  source: string;
  disclaimer?: string;
}

export interface QueueCase {
  caseId: string;
  subject: {
    userId: string;
    displayName: string;
    clusterId: string | null;
    accountStatus: string;
    profileHidden: boolean;
  };
  reason: string;
  reasonLabel: string;
  source: string;
  status: string;
  verdict: string | null;
  evidenceHold: boolean;
  reportCount: number;
  openedAt: string;
  dueAt: string;
  slaMins: number;
  slaLabel: string;
  minutesRemaining: number;
  overdue: boolean;
  triage: TriageAdvisory;
}

export interface QueueResponse {
  cases: QueueCase[];
  counts: { returned: number; overdue: number };
  orderedBy: string;
}

export type SafetyScores = Record<string, number> | null;

export interface CaseReport {
  id: string;
  reporterId: string;
  reason: string;
  detail: string | null;
  threadId: string | null;
  messageId: string | null;
  createdAt: string;
}

export interface CaseMessage {
  id: string;
  threadId: string;
  from: 'subject' | 'other_party';
  senderId: string;
  kind: string;
  body: string | null;
  redacted: boolean;
  reported: boolean;
  safetyScores: SafetyScores;
  createdAt: string;
}

export interface CasePhoto {
  id: string;
  blurhash: string | null;
  position: number;
  removed: boolean;
  safetyScores: SafetyScores;
  expiresAt: string | null;
  url: string;
}

export interface CaseDetailResponse {
  case: {
    id: string;
    reason: string;
    reasonLabel: string;
    source: string;
    status: string;
    verdict: string | null;
    note: string | null;
    assignedTo: string | null;
    evidenceHold: boolean;
    openedAt: string;
    dueAt: string;
    closedAt: string | null;
    slaMins: number;
    slaLabel: string;
    overdue: boolean;
    policyAction: { autoHide: boolean; preserveEvidence: boolean };
  };
  subject: {
    userId: string;
    displayName: string;
    ageYears: number | null;
    gender: string | null;
    pronouns: string | null;
    structure: string | null;
    clusterId: string | null;
    bio: string | null;
    hidden: boolean;
    accountStatus: string;
    onboardingStep: string;
    joinedAt: string;
    lastSeenAt: string | null;
    suspendedAt: string | null;
    bannedAt: string | null;
    banReason: string | null;
    verifications: { kind: string; grantedAt: string; revokedAt: string | null }[];
    photos: CasePhoto[];
    promptAnswers: { id: string; promptId: string; body: string; qualityScore: number | null }[];
    bansOnRecord: number;
  };
  reports: CaseReport[];
  priorCases: {
    id: string;
    reason: string;
    verdict: string | null;
    closedAt: string | null;
    assignedTo: string | null;
  }[];
  messages: CaseMessage[];
  triage: TriageAdvisory;
}

export interface VerdictResponse {
  caseId?: string;
  verdict: string;
  message: string;
  banKeys?: number;
  evidenceHold?: boolean;
  redaction?: { messagesRedacted?: number; photosRemoved?: number };
}

export interface RatioSnapshotRow {
  clusterId: string;
  men: number;
  womenNb: number;
  ratio: number;
  paused: boolean;
}

export interface GuardrailRow {
  key: string;
  label: string;
  value: number | null;
  unit: string;
  threshold: number;
  breachWhen: 'above' | 'below';
  breached: boolean;
  automaticAction: string;
  sample: number;
  minSample: number;
  note?: string;
}

export interface MetricsResponse {
  generatedAt: string;
  northStar: string;
  ratios: RatioSnapshotRow[];
  guardrails: GuardrailRow[];
  breached: string[];
}

export interface ReleaseResponse {
  clusterId: string;
  requested: number;
  released: number;
  releasedUserIds: string[];
  stillWaiting: number;
  intakePaused: boolean;
  menSkipped: number;
  message: string;
}

/**
 * Queue depth per cluster.
 *
 * There is no GET /admin/waitlist in the API as it stands — POST
 * /admin/waitlist/release is the only waitlist route. The console asks for this
 * shape anyway and degrades honestly when the answer is 404, because guessing a
 * depth would be worse than admitting we do not have one. If the endpoint lands
 * later, returning `{ clusters: [{ clusterId, waiting, men, womenNb }] }` is all
 * it needs to do for this screen to light up.
 */
export interface WaitlistDepthRow {
  clusterId: string;
  waiting: number;
  men?: number;
  womenNb?: number;
}

export interface WaitlistDepthResponse {
  clusters: WaitlistDepthRow[];
}

/**
 * Plan pricing (§12).
 *
 * Amounts are paise and GST-inclusive — ₹299 is 29900 — because §12 says the
 * price on the plans screen has to be the price at checkout, or the surprise
 * kills conversion. The console can move the amount and nothing else: duration
 * and what a plan grants are product decisions, not dials, and the API does not
 * accept them.
 */
export interface PricingPlanRow {
  plan: Plan;
  label: string;
  defaultPaise: number;
  currentPaise: number;
  /** True when a console-set price is in force, even if it equals the default. */
  overridden: boolean;
  durationDays: number;
}

export interface PricingResponse {
  plans: PricingPlanRow[];
  note: string;
  /** The handle the API will write against the change — its view, not ours. */
  actor: string;
}

export interface PriceUpdateResponse {
  plan: Plan;
  amountPaise: number;
  previousPaise: number;
  note: string;
}

/* ------------------------------------------------------------------ *
 * Calls
 * ------------------------------------------------------------------ */

export function getQueue(params: { status: 'open' | 'closed' | 'all'; limit: number }): Promise<QueueResponse> {
  const qs = new URLSearchParams({ status: params.status, limit: String(params.limit) });
  return request<QueueResponse>('GET', `/admin/queue?${qs.toString()}`);
}

export function getCase(caseId: string): Promise<CaseDetailResponse> {
  return request<CaseDetailResponse>('GET', `/admin/case/${encodeURIComponent(caseId)}`);
}

export function postVerdict(input: {
  caseId: string;
  verdict: string;
  note: string;
}): Promise<VerdictResponse> {
  return request<VerdictResponse>('POST', '/admin/verdict', input);
}

export function getMetrics(): Promise<MetricsResponse> {
  return request<MetricsResponse>('GET', '/admin/metrics');
}

export function releaseWaitlist(input: { clusterId: string; count: number }): Promise<ReleaseResponse> {
  return request<ReleaseResponse>('POST', '/admin/waitlist/release', input);
}

/** Returns null when the API build has no depth endpoint, rather than throwing. */
export async function getWaitlistDepth(): Promise<WaitlistDepthResponse | null> {
  try {
    const raw = await request<WaitlistDepthResponse | WaitlistDepthRow[]>('GET', '/admin/waitlist');
    if (Array.isArray(raw)) return { clusters: raw };
    if (raw && Array.isArray(raw.clusters)) return raw;
    return null;
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 405 || err.status === 501)) {
      return null;
    }
    throw err;
  }
}

export function getPricing(): Promise<PricingResponse> {
  return request<PricingResponse>('GET', '/admin/pricing');
}

export function setPlanPrice(input: { plan: Plan; amountPaise: number }): Promise<PriceUpdateResponse> {
  return request<PriceUpdateResponse>('POST', '/admin/pricing', input);
}

/**
 * Sign in by using the credential for real.
 *
 * /admin/metrics is the cheapest honest probe: it touches no user record, it
 * answers the question the operator most wants answered on arrival, and its
 * audit row is exactly the "a moderator opened the console" event we want on
 * the record. Its result is handed straight to the shell, so the gate costs
 * nothing extra.
 */
export async function signIn(key: string, actor: string): Promise<MetricsResponse> {
  setCredential(key, actor);
  try {
    return await getMetrics();
  } catch (err) {
    clearCredential();
    throw err;
  }
}
